import { Check, Copy01 } from '@untitledui/icons';
import { useEffect, useRef } from 'react';

import { useClipboard } from '@/hooks/useClipboard';
import { cx } from '@/utils/cx';

import { useCopyAffordance } from './copyAffordance';

export interface CopyOnHoverValueProps {
  /** The text placed on the clipboard. */
  copyText: string;
  /** Accessible label for the copy button, e.g. "Copy phone number". */
  copyLabel: string;
  /** Extra classes on the pill wrapper. */
  className?: string;
  /** The rendered value (link or text). */
  children: React.ReactNode;
  /**
   * When true, allow the pill to shrink and ellipsize inside narrow
   * flex/table columns (adds min-w-0 overflow-hidden). Leave false in
   * inline text flow so the pill keeps overflow:visible and a correct
   * text baseline.
   */
  truncate?: boolean;
}

/**
 * Hover-pill copy affordance shared by contact-detail value labels (phone,
 * email): the value sits in a subtle pill that reveals a copy button on
 * hover, fading the value's tail under a gradient so the button never
 * overlaps text abruptly.
 *
 * One component rather than per-label styling so the pills stay visually
 * identical across field types (see FRONTEND_PATTERNS.md — composition for
 * visual consistency, not repeated classes).
 *
 * Layout notes:
 * - The pill is `inline-block`. `min-w-0 overflow-hidden` are applied ONLY
 *   when `truncate` is true so a narrow flex/table column can shrink-and-
 *   ellipsize the value; they are deliberately OMITTED by default so that in
 *   inline text flow the inline-block keeps `overflow: visible` and reports
 *   its text baseline (CSS 2.1 §10.8.1) — otherwise the value renders raised
 *   above the surrounding `Label:` text.
 * - The negative margins cancel the pill padding so the value's text sits
 *   exactly where a bare label would — surrounding layout doesn't shift.
 */
export const CopyOnHoverValue: React.FC<CopyOnHoverValueProps> = ({
  copyText,
  copyLabel,
  className = '',
  children,
  truncate = false,
}) => {
  const { copied, copy } = useClipboard();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const copyAffordance = useCopyAffordance();

  // Blur the button when copied state returns to false so it hides
  useEffect(() => {
    if (!copied && buttonRef.current) {
      buttonRef.current.blur();
    }
  }, [copied]);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    copy(copyText);
  };

  if (copyAffordance === 'host') {
    // The host surface provides the copy control (see copyAffordance.ts):
    // just the value, keeping only the truncation contract callers rely on.
    return (
      <span
        className={cx(
          'inline-block',
          truncate && 'min-w-0 overflow-hidden',
          className
        )}
      >
        {children}
      </span>
    );
  }

  return (
    <span
      className={cx(
        'group/copyval relative inline-block px-1.5 py-0.5 -mx-1.5 -my-0.5 rounded-md hover:bg-[var(--color-bg-tertiary)] transition-colors',
        truncate && 'min-w-0 overflow-hidden',
        className
      )}
    >
      {children}
      <button
        ref={buttonRef}
        type='button'
        onClick={handleCopy}
        className='absolute right-0 top-1/2 -translate-y-1/2 opacity-0 pointer-events-none group-hover/copyval:opacity-100 group-hover/copyval:pointer-events-auto transition-opacity focus:opacity-100 focus:pointer-events-auto flex items-center pl-6 pr-1.5 py-0.5 bg-gradient-to-r from-transparent via-[var(--color-bg-tertiary)] to-[var(--color-bg-tertiary)] rounded-r-md'
        aria-label={copied ? 'Copied' : copyLabel}
      >
        {copied ? (
          <Check className='w-3.5 h-3.5 text-success-600' />
        ) : (
          <Copy01 className='w-3.5 h-3.5 text-fg-secondary' />
        )}
      </button>
    </span>
  );
};
